// Interceptor para refrescar el access token automáticamente
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    // Si es 401 y no es un intento de refresco
    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry
    ) {
      const refreshToken = localStorage.getItem("refresh_token");
      if (refreshToken) {
        originalRequest._retry = true;
        try {
          const res = await axios.post(
            `${Vue.prototype.$hostname}/api/token/refresh/`,
            {
              refresh: refreshToken,
            },
          );
          const newToken = res.data.access;
          localStorage.setItem("token", newToken);
          axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
          originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
          return axios(originalRequest);
        } catch (refreshError) {
          // Si falla el refresh, limpiar tokens y redirigir a login
          localStorage.removeItem("token");
          localStorage.removeItem("refresh_token");
          window.location.href = "/";
        }
      }
    }
    return Promise.reject(error);
  },
);
import Vue from "vue";
import App from "./App.vue";
import router from "./router";

import { library } from "@fortawesome/fontawesome-svg-core";
import {
  faCamera,
    faCalculator,
    faInbox,
    faLightbulb,
    faList,
    faMap,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

// import axios
import { Capacitor } from "@capacitor/core";
import axios from "axios";
import {
    cacheGet,
    cacheSet,
    enqueueOperation,
    initNetworkListener,
    networkState,
} from "./services/offlineService";

library.add(faMap);
library.add(faInbox);
library.add(faCalculator);
library.add(faList);
library.add(faLightbulb);
library.add(faCamera);

Vue.component("font-awesome-icon", FontAwesomeIcon);

// set a prototype for http
Vue.prototype.$http = axios;
Vue.config.productionTip = false;

// Determinar hostname basado en el entorno
const getHostname = () => {
  if (Capacitor.isNativePlatform()) {
    // Si es una app móvil, usar siempre producción
    return "https://api.production.agroairplatform.es";
  }
  // Si es web, usar la lógica basada en NODE_ENV
  return process.env.NODE_ENV === "production"
    ? "https://api.production.agroairplatform.es"
    : "http://localhost:8000";
};

Vue.prototype.$hostname = getHostname();
const token = localStorage.getItem("token");
if (token) {
  axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
}

// ── Modo offline (solo plataformas nativas: Android/iOS) ──────────────────────
if (Capacitor.isNativePlatform()) {
  const NEVER_QUEUE = ["/api/token/", "/api/token/refresh/"];

  // Interceptor de REQUEST
  axios.interceptors.request.use(async (config) => {
    // Sin conexión + GET → intentar caché
    if (!networkState.isOnline && config.method === "get") {
      const cached = await cacheGet(config.url);
      if (cached !== null) {
        return Promise.reject({ __fromCache: true, data: cached, config });
      }
      return Promise.reject(new Error("Sin conexión y sin caché disponible"));
    }

    // Sin conexión + escritura → encolar (excepto login/refresh)
    if (
      !networkState.isOnline &&
      ["post", "put", "patch", "delete"].includes(config.method)
    ) {
      const shouldQueue = !NEVER_QUEUE.some((path) =>
        config.url.includes(path),
      );
      if (!shouldQueue) {
        return Promise.reject(
          new Error("Esta operación requiere conexión a internet"),
        );
      }
      await enqueueOperation({
        method: config.method,
        url: config.url,
        data: config.data,
        headers: config.headers,
      });
      return Promise.reject({
        __queued: true,
        message: "Operación guardada. Se enviará cuando recuperes la conexión.",
        config,
      });
    }

    return config;
  });

  // Interceptor de RESPONSE
  axios.interceptors.response.use(
    async (response) => {
      // Cachear respuestas GET exitosas
      if (response.config.method === "get") {
        await cacheSet(response.config.url, response.data);
      }
      return response;
    },
    async (error) => {
      // Respuesta desde caché (no es un error real)
      if (error.__fromCache) {
        return Promise.resolve({ data: error.data, __fromCache: true });
      }
      // Operación encolada (no es un error real)
      if (error.__queued) {
        return Promise.resolve({
          data: null,
          __queued: true,
          message: error.message,
        });
      }
      // Error de red real sin respuesta → intentar caché para GETs
      if (!error.response && error.config && error.config.method === "get") {
        const cached = await cacheGet(error.config.url);
        if (cached !== null) {
          return Promise.resolve({ data: cached, __fromCache: true });
        }
      }
      return Promise.reject(error);
    },
  );

  // Iniciar listener de red
  initNetworkListener();
}

// Exponer estado de red globalmente (en web siempre será { isOnline: true })
Vue.prototype.$networkState = networkState;

// Importing styles
import "./assets/css/animations.css";
import "./assets/css/componets.css";
import "./assets/css/custom.css";
import "./assets/css/footer.css";
import "./assets/css/global.css";
import "./assets/css/header.css";
import "./assets/css/views.css";

new Vue({
  router,
  render: (h) => h(App),
}).$mount("#app");
